import { Link } from 'react-router-dom';
import SEO from '../components/common/SEO';
import { homeMetadata } from '../config/metadata';
import styles from './Home.module.css';

const exampleRequests = [
  {
    title: 'Every hospital and outpatient clinic transaction in California since 2020',
    tag: 'M&A',
  },
  {
    title: 'Every PE-backed roll-up of veterinary clinics in the Midwest since 2018',
    tag: 'Private Equity',
  },
  {
    title: 'Every software company sold to a strategic buyer by a PE sponsor in the last five years',
    tag: 'Private Equity',
  },
  {
    title: 'Every seed and Series A round raised by data center infrastructure startups in North America',
    tag: 'Venture Capital',
  },
  {
    title: 'Every follow-on investment made by a defined set of growth equity funds into SaaS companies',
    tag: 'Venture Capital',
  },
  {
    title: 'Every defense contractor acquisition involving private capital since 2017',
    tag: 'M&A',
  },
];

const audiences = [
  {
    title: 'Private equity firms',
    copy: 'Map sector consolidation, track competing sponsors, and build target lists grounded in verified transaction history.',
  },
  {
    title: 'Venture capital investors',
    copy: 'See who funded what, at what stage, and when. Round-by-round histories for a sector, geography, or investor set.',
  },
  {
    title: 'Investment banks',
    copy: 'Support pitches and fairness work with precedent transactions, multiples, and advisor activity in a defined market.',
  },
  {
    title: 'M&A advisors',
    copy: 'Understand buyer behavior in a niche before going to market, including financing patterns and follow-on activity.',
  },
  {
    title: 'Consultants',
    copy: 'Back client recommendations with a clean, sourced dataset instead of weeks of analyst screen time.',
  },
  {
    title: 'Corporate development teams',
    copy: 'Monitor acquisition activity in your space and study how sponsors are valuing businesses like yours.',
  },
];

const datasetFields = [
  { field: 'Buyer and seller', detail: 'Legal entities, sponsors, and platforms behind each transaction' },
  { field: 'Acquisition date', detail: 'Announcement and close dates where both are disclosed' },
  { field: 'Transaction value', detail: 'Disclosed or estimated value with the basis clearly noted' },
  { field: 'Revenue and EBITDA', detail: 'Financials gathered from filings, press, and industry sources' },
  { field: 'Valuation multiple', detail: 'Computed multiples with the underlying inputs shown' },
  { field: 'Advisors', detail: 'Bankers, lawyers, and accountants on each side of the deal' },
  { field: 'Financing', detail: 'Debt packages, lenders, and equity structure where disclosed' },
  { field: 'Subsequent acquisitions', detail: 'Add-ons and follow-on rounds after the initial transaction' },
  { field: 'Source documentation', detail: 'A citation trail for every material data point' },
];

const processSteps = [
  {
    step: '01',
    title: 'Scope',
    copy: 'You share the question with us. We confirm the definition of the dataset, the fields you need, and the timeline before any work begins.',
  },
  {
    step: '02',
    title: 'Research',
    copy: 'Our team works across filings, announcements, registries, trade press, and investor disclosures to assemble the underlying records.',
  },
  {
    step: '03',
    title: 'Verify',
    copy: 'Every record is cross-checked against at least one independent source. Estimates are labeled and sourcing is documented row by row.',
  },
  {
    step: '04',
    title: 'Deliver',
    copy: 'You receive a clean, structured file with source documentation, plus a walkthrough of methodology and coverage notes.',
  },
];

const sources = [
  'Institutional database seats such as PitchBook and CapIQ',
  'Paid industry and trade association reports',
  'Licensed conference and contributor datasets',
  'Fee-based corporate and specialty registries',
  'SEC filings and regulatory disclosures',
  'Company, PE, and VC firm announcements',
  'Bank, law firm, and advisor deal reports',
  'Niche vertical media and market studies',
];

const faqs = [
  {
    q: 'How long does a project take?',
    a: 'A focused dataset typically takes 2 to 3 weeks. Smaller, tightly scoped projects can be completed in 5 to 7 days. We confirm the timeline during scoping before you commit.',
  },
  {
    q: 'What does a project cost?',
    a: 'Most customized datasets and research projects run between $3,000 and $6,000. Larger or more complex projects can exceed $10,000. Pricing is fixed during scoping, so there are no surprises.',
  },
  {
    q: 'Do you cover venture capital deals?',
    a: 'Yes. We build datasets on venture rounds, investor activity, and startup funding histories using the same verification standards we apply to buyout and M&A research.',
  },
  {
    q: 'What format is the deliverable?',
    a: 'A structured spreadsheet or CSV with a documented column dictionary, plus source citations for each record. We can adapt the format to your internal templates.',
  },
  {
    q: 'How is the data verified?',
    a: 'Each record is cross-referenced against independent sources before it enters the file. Where values are estimated, the estimate basis is labeled so your team can judge reliability.',
  },
  {
    q: 'What if some deal details are not publicly disclosed?',
    a: 'Private-market filings and press often leave gaps. We document every blank clearly, note what was searched, and flag estimates separately from confirmed figures so your team can see exactly what is known versus inferred.',
  },
  {
    q: 'Can you update a dataset after delivery?',
    a: 'Yes. Many clients come back for a refresh when they need the same cut of the market brought current. Refresh projects are scoped separately and are usually faster than the original build.',
  },
  {
    q: 'Do you work under NDA?',
    a: 'Yes. We routinely sign mutual NDAs before discussing sensitive sectors, target lists, or internal thesis details. Raise it when we follow up on your request.',
  },
  {
    q: 'Can you research international markets?',
    a: 'Yes. Coverage depends on the disclosure environment in each country. We flag early if a geography has thin public reporting so you can adjust scope before work begins.',
  },
  {
    q: 'Who owns the finished dataset?',
    a: 'You do. The deliverable is yours to use internally. If you need broader redistribution rights for clients or published materials, we can address that in the engagement terms.',
  },
  {
    q: 'How do I get started?',
    a: 'Share the question you need answered, along with sector, geography, and time period if you have them. We review fit and reply with scope, timeline, and pricing, usually within one business day.',
  },
];

export default function Home() {
  return (
    <>
      <SEO
        title={homeMetadata.title}
        description={homeMetadata.description}
        path="/"
        keywords={homeMetadata.keywords}
      />

      {/* Hero */}
      <section className={styles.hero} aria-labelledby="hero-heading">
        <img
          className={styles.heroPhoto}
          src="/hero-home.jpg"
          alt=""
          aria-hidden="true"
        />
        <div className={styles.heroInner}>
          <div className={styles.heroText}>
            <p className={styles.brandMark}>Aedra</p>
            <h1 id="hero-heading" className={styles.headline}>
              Private-market questions, answered with data.
            </h1>
            <p className={styles.lede}>
              Aedra builds custom, source-verified private-market datasets on PE, VC, and M&A activity for deal teams and research professionals.
            </p>
            <div className={styles.ctaRow}>
              <Link to="/capabilities" className={styles.primaryCta}>
                Explore capabilities
              </Link>
            </div>
            <ul className={styles.heroProof}>
              <li>
                <strong>Project-based</strong>
                <span>no platform seat required</span>
              </li>
              <li>
                <strong>Source-verified</strong>
                <span>every record cited</span>
              </li>
              <li>
                <strong>2 to 3 weeks</strong>
                <span>typical turnaround</span>
              </li>
            </ul>
          </div>

          <aside className={styles.heroCard} aria-label="Example dataset requests">
            <p className={styles.heroCardTitle}>Recent request examples</p>
            <ul className={styles.heroCardList}>
              {exampleRequests.slice(0, 4).map((item) => (
                <li key={item.title}>
                  <span className={styles.heroCardTag}>{item.tag}</span>
                  {item.title}
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </section>

      {/* Who we serve */}
      <section className={styles.section} aria-labelledby="who-heading">
        <div className={styles.sectionInner}>
          <p className={styles.eyebrow}>Who we serve</p>
          <h2 id="who-heading" className={styles.sectionTitle}>
            Research support for investment professionals
          </h2>
          <p className={styles.sectionCopy}>
            Aedra works with teams across the private markets that need reliable transaction data on a specific sector, geography, or investor group.
          </p>
          <div className={styles.cardGrid}>
            {audiences.map((item) => (
              <article key={item.title} className={styles.card}>
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Example datasets */}
      <section className={styles.sectionAlt} aria-labelledby="examples-heading">
        <div className={styles.sectionInner}>
          <p className={styles.eyebrow}>What we build</p>
          <h2 id="examples-heading" className={styles.sectionTitle}>
            Datasets scoped to a single question
          </h2>
          <p className={styles.sectionCopy}>
            Each engagement starts with a precise research question. The examples below reflect the level of specificity we work at across buyouts, venture rounds, and M&A.
          </p>
          <div className={styles.exampleGrid}>
            {exampleRequests.map((item) => (
              <div key={item.title} className={styles.exampleCard}>
                <span className={styles.exampleTag}>{item.tag}</span>
                <p>{item.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dataset fields */}
      <section className={styles.section} aria-labelledby="fields-heading">
        <div className={styles.sectionInner}>
          <p className={styles.eyebrow}>Coverage</p>
          <h2 id="fields-heading" className={styles.sectionTitle}>
            What each dataset can include
          </h2>
          <p className={styles.sectionCopy}>
            Fields are scoped to your question, so the file gives you depth where it matters. Typical columns include:
          </p>
          <div className={styles.fieldGrid}>
            {datasetFields.map((item) => (
              <div key={item.field} className={styles.fieldCard}>
                <h3>{item.field}</h3>
                <p>{item.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className={styles.sectionDark} id="process" aria-labelledby="process-heading">
        <div className={styles.sectionInner}>
          <p className={styles.eyebrowLight}>Process</p>
          <h2 id="process-heading" className={styles.sectionTitleLight}>
            How an engagement works
          </h2>
          <p className={styles.sectionCopyLight}>
            A defined, four-step process from intake to delivery. You know the scope, cost, and timeline before research begins.
          </p>
          <div className={styles.processGrid}>
            {processSteps.map((item) => (
              <div key={item.step} className={styles.processCard}>
                <span className={styles.processStep}>{item.step}</span>
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why it matters */}
      <section className={styles.section} aria-labelledby="why-heading">
        <div className={styles.splitInner}>
          <div>
            <p className={styles.eyebrow}>Why teams hire us</p>
            <h2 id="why-heading" className={styles.sectionTitle}>
              The best answers sit behind expensive access
            </h2>
            <p className={styles.sectionCopy}>
              Useful private-market evidence is rarely free or neatly packaged. Firms already spend thousands each year on database seats, paid industry reports, conference intelligence products, and fee-based registries. Even then, the cut you need may still require weeks of analyst time to extract, reconcile, and document.
            </p>
            <p className={styles.sectionCopy}>
              Aedra is hired to do that synthesis: pull across licensed and public sources, verify the records, label estimates, and return one finished file for the question on your desk.
            </p>
            <p className={styles.sectionCopy}>
              <Link to="/capabilities" className={styles.inlineLink}>
                See the types of data we build →
              </Link>
            </p>
          </div>
          <div className={styles.sourcePanel}>
            <p className={styles.sourcePanelTitle}>Sources we routinely work across</p>
            <ul className={styles.sourceList}>
              {sources.map((source) => (
                <li key={source}>{source}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className={styles.sectionAlt} aria-labelledby="diff-heading">
        <div className={styles.splitInner}>
          <div>
            <p className={styles.eyebrow}>How Aedra compares</p>
            <h2 id="diff-heading" className={styles.sectionTitle}>
              Built around your question
            </h2>
            <p className={styles.sectionCopy}>
              Platforms like PitchBook, Capital IQ, Crunchbase, SourceScrub, and Grata are designed for broad coverage and recurring screening across a firm. Aedra is commissioned for the specific question those tools leave unfinished: a niche cut, a documented comps set, or a verified history your team can use the day it arrives.
            </p>
            <p className={styles.sectionCopy}>
              Because every engagement is custom, coverage goes as deep as your question requires, including niches and regions where subscription databases are thin. For a fuller comparison, see our{' '}
              <Link to="/capabilities" className={styles.inlineLink}>
                Capabilities
              </Link>{' '}
              page.
            </p>
          </div>
          <aside className={styles.statPanel} aria-label="Engagement details">
            <div className={styles.stat}>
              <span className={styles.statValue}>2 to 3 weeks</span>
              <span className={styles.statLabel}>Typical turnaround</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>5 to 7 days</span>
              <span className={styles.statLabel}>Smaller projects</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>$3,000 to $6,000</span>
              <span className={styles.statLabel}>Typical project range</span>
            </div>
            <p className={styles.statNote}>Larger or more complex projects can exceed $10,000. Scope and price are fixed before work begins.</p>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      <section className={styles.section} aria-labelledby="faq-heading">
        <div className={styles.sectionInner}>
          <p className={styles.eyebrow}>FAQ</p>
          <h2 id="faq-heading" className={styles.sectionTitle}>
            Common questions
          </h2>
          <div className={styles.faqList}>
            {faqs.map((item) => (
              <details key={item.q} className={styles.faqItem}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className={styles.ctaBand} aria-labelledby="cta-band-heading">
        <div className={styles.ctaBandInner}>
          <h2 id="cta-band-heading" className={styles.ctaBandTitle}>
            Ready to scope a dataset?
          </h2>
          <p className={styles.ctaBandCopy}>
            Share the private-market question you need answered. We will confirm fit, scope, and timeline within one business day.
          </p>
          <Link to="/capabilities" className={styles.primaryCta}>
            Explore capabilities
          </Link>
        </div>
      </section>
    </>
  );
}
